import { LocationItem } from '../../../types/search';
import { SearchService } from '../services/searchService';
import { highlightText } from '../utils';
import { debounceScrollLoad } from '../utils';

interface ModalHandlersProps {
  setOpenModal: (open: boolean) => void;
  setNavigationHistory: (history: LocationItem[]) => void;
  setSelectedMainOption: (option: LocationItem | null) => void;
  setModalView: (view: 'main' | 'sub') => void;
  setModalSearchValue: (value: string) => void;
  setLoadingLocationData: (loading: boolean) => void;
  setIsLoadingMore: (loading: boolean) => void;
  setLocationData: (data: LocationItem[]) => void;
  setHasMore: (hasMore: boolean) => void;
  setPage: (page: number) => void;
  setSelectedLocation: (location: LocationItem | null) => void;
  setSelectedSubLocation: (location: LocationItem | null) => void;
  setSearchText: (text: string) => void;
  setShowDropdown: (show: boolean) => void;

  navigationHistory: LocationItem[];
  selectedMainOption: LocationItem | null;
  modalView: 'main' | 'sub';
  modalSearchValue: string;
  locationTypeId: number | null;
  transactionType: string;
  propertyType: string;
  locationData: LocationItem[];
  page: number;
  hasMore: boolean;
  loadingLocationData: boolean;
  isLoadingMore: boolean;

  getActionIdFromType: (type: string) => number | null;
  getCategoryIdFromType: (type: string) => number | null;
  loadLocationDataBySearch: (typeId: number, searchValue: string) => Promise<void>;
  
  locale: string;
}

export const createModalHandlers = (props: ModalHandlersProps) => {
  const handleModalClose = () => {
    props.setNavigationHistory([]);
    props.setSelectedMainOption(null);
    props.setModalView('main');
    props.setModalSearchValue('');
    props.setOpenModal(false);
  };

  const handleBackToMain = () => {
    if (props.navigationHistory.length > 0) {
      const previousOption = props.navigationHistory[props.navigationHistory.length - 1];
      const newHistory = props.navigationHistory.slice(0, -1);
      
      props.setSelectedMainOption(previousOption);
      props.setNavigationHistory(newHistory);
      
      if (newHistory.length === 0) {
        props.setModalView('main');
      }
    } else {
      props.setModalView('main');
      props.setSelectedMainOption(null);
    }
    
    props.setModalSearchValue('');
  };

  const handleMainOptionClick = (location: LocationItem) => {
    if (props.selectedMainOption) {
      props.setNavigationHistory(prev => [...prev, props.selectedMainOption]);
    }
    
    props.setSelectedMainOption(location);
    props.setModalView('sub');
    
    if (location.children && location.children.length > 0) {
      return;
    }
    
    const isProvinceLocation = (location.typeId === 0 || location.typeId === null) || location.isAmphur || location.isDistrict;
    
    if (isProvinceLocation) {
      props.setLoadingLocationData(true);
      
      import('../provinceLocationHandler').then(module => {
        const { loadProvinceSubLocations } = module;
        
        loadProvinceSubLocations(
          location,
          props.modalSearchValue,
          props.setLoadingLocationData,
          props.setSelectedMainOption,
          props.locale
        );
      }).catch(error => {
        props.setLoadingLocationData(false);
      });
    } 
    else if (location.typeId !== -1) {
      props.setLoadingLocationData(true);
      
      import('../loadSubLocations').then(module => {
        const { loadSubLocations } = module;
        
        loadSubLocations(
          location,
          props.modalSearchValue,
          props.setLoadingLocationData,
          props.setSelectedMainOption,
          props.locale
        );
      }).catch(error => {
        props.setLoadingLocationData(false);
      });
    }
  };

  const handleSelectSubLocation = (parentLocation: LocationItem, subLocation: LocationItem) => {
    if (parentLocation && subLocation) {
      props.setSelectedLocation(parentLocation);
      props.setSelectedSubLocation(subLocation);
      
      let searchTextValue = '';
      
      if (parentLocation) {
        if (subLocation.value && subLocation.value.toString().includes('all_')) {
          // กรณีเลือก "ทุกเขต/ทุกอำเภอ" หรือ "ทุกแขวง/ทุกตำบล"
          if (subLocation.value.toString().includes('all_districts_')) {
            // กรณีเลือก "ทุกเขต/ทุกอำเภอ" → แสดงแค่ชื่อจังหวัด
            searchTextValue = parentLocation.label;
          } else if (subLocation.value.toString().includes('all_subdistricts_')) {
            // กรณีเลือก "ทุกแขวง/ทุกตำบล" → แสดง "จังหวัด - อำเภอ"
            if (parentLocation.isAmphur && parentLocation.parentName) {
              searchTextValue = `${parentLocation.parentName} - ${parentLocation.label}`;
            } else {
              searchTextValue = parentLocation.label;
            }
          } else if (parentLocation.isAmphur && parentLocation.parentName) {
            // หากเป็นอำเภอ ให้แสดง "จังหวัด - อำเภอ"
            searchTextValue = `${parentLocation.parentName} - ${parentLocation.label}`;
          } else if (props.navigationHistory.length > 0) {
            // หากมี navigation history ให้ใช้ชื่อจากประวัติการเลือก
            const provinceFromHistory = props.navigationHistory[0]; // อันแรกควรเป็นจังหวัด
            searchTextValue = `${provinceFromHistory.label} - ${parentLocation.label}`;
          } else {
            // กรณีอื่นๆ ให้แสดงแค่ parent location
            searchTextValue = parentLocation.label;
          }
        } else {
          // กรณีเลือกตำบลเฉพาะ
          if (subLocation.isDistrict && parentLocation.isAmphur) {
            // หากเป็นตำบลและ parent เป็นอำเภอ ให้แสดง "จังหวัด - อำเภอ - ตำบล"
            const provinceName = parentLocation.parentName || 'จังหวัด';
            searchTextValue = `${provinceName} - ${parentLocation.label} - ${subLocation.originalLabel || subLocation.label}`;
          } else if (parentLocation.isAmphur && parentLocation.parentName) {
            // หากเป็นอำเภอและมีชื่อจังหวัด ให้แสดง "จังหวัด - อำเภอ - ตำบล"
            searchTextValue = `${parentLocation.parentName} - ${parentLocation.label} - ${subLocation.originalLabel || subLocation.label}`;
          } else if (props.navigationHistory.length > 0) {
            // หากมี navigation history ให้ใช้ชื่อจากประวัติการเลือก
            const provinceFromHistory = props.navigationHistory[0]; // อันแรกควรเป็นจังหวัด
            if (props.navigationHistory.length >= 2) {
              // มีทั้งจังหวัดและอำเภอใน history
              const amphurFromHistory = props.navigationHistory[1];
              searchTextValue = `${provinceFromHistory.label} - ${amphurFromHistory.label} - ${subLocation.originalLabel || subLocation.label}`;
            } else {
              // มีแค่จังหวัดใน history
              searchTextValue = `${provinceFromHistory.label} - ${parentLocation.label} - ${subLocation.originalLabel || subLocation.label}`;
            }
          } else {
            // กรณีอื่นๆ ให้แสดงแค่ parent - sub
            searchTextValue = `${parentLocation.label} - ${subLocation.originalLabel || subLocation.label}`;
          }
        }
      }
      
      props.setSearchText(searchTextValue);
      props.setShowDropdown(false);
      props.setOpenModal(false);
      
      if (props.locationTypeId === -1) {
        const selectedActionId = props.getActionIdFromType(props.propertyType);
        const selectedCategoryId = props.getCategoryIdFromType(props.propertyType);
        
        import('../../../utils/requests').then(module => {
          const { getProjectNameData } = module;
          
          getProjectNameData(
            selectedActionId, 
            selectedCategoryId, 
            subLocation.label,
            props.locale, 
            1, 
            1
          ).then(result => {
          }).catch(error => {
          });
        });
      }
    }
  };

  const handleLocationSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value.toLowerCase();
    props.setModalSearchValue(e.target.value);

    if (props.modalView === 'main') {
      if (props.locationTypeId !== null && props.locationTypeId !== undefined) {
        props.loadLocationDataBySearch(props.locationTypeId, searchValue);
      }
    } else if (props.modalView === 'sub' && props.selectedMainOption) {
      if (props.selectedMainOption.typeId === 0 || props.selectedMainOption.isAmphur || props.selectedMainOption.isDistrict) {
        import('../provinceLocationHandler').then(module => {
          const { loadProvinceSubLocations } = module;
          
          loadProvinceSubLocations(
            props.selectedMainOption!,
            e.target.value,
            props.setLoadingLocationData,
            props.setSelectedMainOption,
            props.locale
          );
        }).catch(error => {
        });
      } else if (props.selectedMainOption.children && props.selectedMainOption.children.length > 0) {
        searchSubLocations(props.selectedMainOption, searchValue);
      }
    }
  };

  const searchSubLocations = (parentLocation: LocationItem, searchQuery: string) => {
    if (!parentLocation.children || !Array.isArray(parentLocation.children)) {
      return;
    }

    const filteredChildren = parentLocation.children.filter(child => {
      const childLabel = child.label || '';
      return childLabel.toLowerCase().includes(searchQuery.toLowerCase());
    });

    const highlightedChildren = filteredChildren.map(child => {
      const highlighted = highlightText(child.label, searchQuery);
      return {
        ...child,
        displayLabel: highlighted.displayLabel,
        originalLabel: highlighted.originalLabel,
        rendered: highlighted.rendered
      };
    });

    const updatedLocation = {
      ...parentLocation,
      children: highlightedChildren.length > 0 ? highlightedChildren : parentLocation.children
    };

    props.setSelectedMainOption(updatedLocation);
  };

  const handleSaveSettings = () => {
    // เมื่อกดปุ่ม "บันทึกการตั้งค่า" ให้ทำงานเหมือนการเลือก "ทั้งหมด" ในระดับนั้น
    if (props.selectedMainOption) {
      let searchTextValue = '';
      
      if (props.selectedMainOption.isAmphur && props.selectedMainOption.parentName) {
        // หากอยู่ในหน้าอำเภอ (ตำบล) ให้แสดง "จังหวัด - อำเภอ"
        searchTextValue = `${props.selectedMainOption.parentName} - ${props.selectedMainOption.label}`;
      } else if (props.navigationHistory.length > 0) {
        // หากมี navigation history ให้ใช้ชื่อจากประวัติการเลือก
        const provinceFromHistory = props.navigationHistory[0]; // อันแรกควรเป็นจังหวัด
        searchTextValue = `${provinceFromHistory.label} - ${props.selectedMainOption.label}`;
      } else {
        // กรณีอื่นๆ ให้แสดงแค่ selected option
        searchTextValue = props.selectedMainOption.label;
      }
      
      props.setSearchText(searchTextValue);
      props.setSelectedLocation(props.selectedMainOption);
      props.setSelectedSubLocation(null); // ไม่เลือก sub location เฉพาะ
    }
    
    props.setOpenModal(false);
  };

  const hasSubLocations = async (location: LocationItem): Promise<boolean> => {
    if (location.typeId === -1 || props.locationTypeId === -1) {
      return false;
    }

    if (location.typeId === 0) return true;

    if (location.children && location.children.length > 0) return true;

    return false;
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (props.loadingLocationData || props.isLoadingMore || !props.hasMore) return;

    debounceScrollLoad(event, () => {
      if (props.modalView === 'main' && props.locationTypeId !== null && props.locationTypeId !== undefined && props.locationTypeId === -1) {
        loadMoreProjectData();
      }
    });
  };

  const loadMoreProjectData = async () => {
    if (props.loadingLocationData || props.isLoadingMore || !props.hasMore) return;

    try {
      props.setIsLoadingMore(true);

      const result = await SearchService.loadMoreProjectData(
        props.modalSearchValue,
        props.transactionType,
        props.propertyType,
        props.locale,
        props.page,
        props.locationData,
        props.getActionIdFromType,
        props.getCategoryIdFromType
      );

      props.setLocationData(result.data);
      props.setHasMore(result.hasMore);
      props.setPage(result.newPage);

    } catch (error) {
      console.error('Error loading more project data:', error);
    } finally {
      props.setIsLoadingMore(false);
    }
  };

  const handleFinalSelect = (location: LocationItem) => {
    props.setSelectedLocation(location);
    props.setSelectedSubLocation(null);
    props.setSearchText(location.originalLabel || location.label);
    props.setShowDropdown(false);
    props.setOpenModal(false);
  };

  const shouldShowArrow = (location: LocationItem): boolean => {
    if (location.typeId === -1 || props.locationTypeId === -1) {
      return false;
    }

    if (location.typeId === 0 && !location.isDistrict) {
      return true;
    }
    
    if (location.isAmphur) {
      return true;
    }
    
    if (location.isDistrict) {
      return false;
    }

    if (location.label === 'ไม่มีชื่อ' || location.label === 'ทั้งหมด') {
      return false;
    }

    if (location.children && location.children.length > 0) {
      return true;
    }

    return false;
  };

  return {
    handleModalClose,
    handleBackToMain,
    handleMainOptionClick,
    handleSelectSubLocation,
    handleLocationSearch,
    handleSaveSettings,
    hasSubLocations,
    handleScroll,
    handleFinalSelect,
    shouldShowArrow
  };
}; 